'use client';

import ProductCouponDisplay from '@c/ProductCouponDisplay';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import {
  cn,
  formatCurrency,
  getCountryLabelFromCode,
  isMobile,
} from '@util/index';
import DesktopEstimateShipping from 'app/product/[slug]/components/EstimateShipping/DesktopEstimateShipping';
import MobileEstimateShipping from 'app/product/[slug]/components/EstimateShipping/MobileEstimateShipping';
import { ChevronDownIcon, RefreshCwIcon } from 'lucide-react';
import {
  isVehicleCategory,
  type ProductDocument,
  type Variation,
} from 'models/product';
import { useState } from 'react';

const ProductPriceAndShipping = ({
  product,
  selectedVariations,
  shipping,
  customInputsEnabled,
}: {
  product: ProductDocument;
  selectedVariations?: Variation[] | null;
  shipping?: {
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null;
  customInputsEnabled?: boolean;
}) => {
  const { highestBid } = useRealTimeBids(product.id);
  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const [showMobileEstimateShipping, setShowMobileEstimateShipping] =
    useState(false);
  const [showDesktopEstimateShipping, setShowDesktopEstimateShipping] =
    useState(false);
  const [estimatedShipping, setEstimatedShipping] = useState<{
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null>(null);

  const getPrice = () => {
    if (product.is_auction) {
      return highestBid?.bid_price || product.price;
    }
    let basePrice = Math.max(
      product.price,
      Math.max(...(product.variations?.map((v) => v.price) ?? [0]))
    );

    // Gear combo default (no selection): price = cheapest letter + cheapest
    // number in stock. The plain max(variations) picks a single-side variation
    // which is not the real asking price for a combo.
    const isGearCombo = product.category2 === 'Gear Combos';
    if (
      isGearCombo &&
      product.has_variations &&
      !selectedVariations?.length &&
      product.variations?.length
    ) {
      const inStock = product.variations.filter((v) => v.qty > 0);
      const letters = inStock.filter((v) => !v.is_number);
      const numbers = inStock.filter((v) => v.is_number);
      if (letters.length && numbers.length) {
        basePrice =
          Math.min(...letters.map((v) => v.price)) +
          Math.min(...numbers.map((v) => v.price));
      }
    }

    if (selectedVariations) {
      const letterSize = selectedVariations?.find((v) => Number.isNaN(+v.size));
      const numberSize = selectedVariations?.find(
        (v) => !Number.isNaN(+v.size) && v.is_number
      );
      const isColor = selectedVariations?.find((v) => !!v.color);
      if (letterSize && numberSize && !isColor) {
        basePrice = letterSize.price + numberSize.price;
      } else if (letterSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === letterSize.size
            )?.price || letterSize.price;
        } else {
          basePrice = letterSize.price;
        }
      } else if (numberSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === numberSize.size
            )?.price || numberSize.price;
        } else {
          basePrice = numberSize.price;
        }
      }
    }

    if (customInputsEnabled && product.custom_inputs) {
      basePrice += product.custom_inputs.cost;
    }

    return basePrice;
  };

  // A listing stores a single original price; variations only carry their own
  // sale price, so "Was" stays fixed while the discount varies per variation.
  const ogPrice = product.og_price;
  const salePercent = Math.round(
    ((Number(ogPrice) - getPrice()) / Number(ogPrice)) * 100
  );
  const displayedShipping = estimatedShipping ?? shipping;
  const hasValidDiscount =
    product.on_sale &&
    !product.is_auction &&
    salePercent > 0 &&
    (!product.has_variations || selectedVariations?.length);
  const isFreeShipping =
    (product.is_flat_rate &&
      product.shipping_costs?.every(({ cost }) => cost === 0)) ||
    displayedShipping?.cost === 0;

  // Warehouse products are single items in a group — always show the current
  // product's price, not a range. Variation-priced (non-warehouse) products
  // show a range only when no variation is selected and prices differ.
  const isWarehouseProduct = !!product.group_id;
  const variationPriceRange = (() => {
    if (
      isWarehouseProduct ||
      product.is_auction ||
      !product.has_variations ||
      !product.variations?.length ||
      selectedVariations?.length
    ) {
      return null;
    }

    const inStock = product.variations.filter((v) => v.qty > 0);
    if (!inStock.length) return null;

    // Gear combos price = letter-size + number-size; range must sum both pools.
    const isGearCombo =
      product.category2 === 'Gear Combos' &&
      inStock.some((v) => v.is_number) &&
      inStock.some((v) => !v.is_number);

    if (isGearCombo) {
      const numberPrices = inStock
        .filter((v) => v.is_number)
        .map((v) => v.price)
        .filter((p) => p > 0);
      const letterPrices = inStock
        .filter((v) => !v.is_number)
        .map((v) => v.price)
        .filter((p) => p > 0);
      if (!numberPrices.length || !letterPrices.length) return null;
      const minPrice = Math.min(...numberPrices) + Math.min(...letterPrices);
      const maxPrice = Math.max(...numberPrices) + Math.max(...letterPrices);
      if (minPrice === maxPrice) return null;
      return { minPrice, maxPrice };
    }

    const prices = inStock.map((v) => v.price).filter((p) => p > 0);
    if (!prices.length) return null;
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) return null;

    return { minPrice, maxPrice };
  })();

  return (
    <div className={cn('flex flex-col gap-y-6')}>
      <div className="flex flex-col gap-y-2">
        {hasValidDiscount && (
          <div className="mb-4 w-fit rounded-[1rem] bg-brand-secondary px-6 py-2 text-[1.8rem] font-semibold text-white">
            {salePercent}% OFF
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          {variationPriceRange ? (
            <p className="text-[3rem] font-semibold leading-[2.4rem] text-black">
              {formatCurrency(variationPriceRange.minPrice)} -{' '}
              {formatCurrency(variationPriceRange.maxPrice)}
            </p>
          ) : (
            <>
              <p
                className={cn(
                  'text-[3rem] font-semibold leading-[2.4rem]',
                  product.on_sale
                    ? 'text-brand-secondary'
                    : 'text-brand-primary'
                )}
              >
                {formatCurrency(getPrice())}
              </p>

              {hasValidDiscount && (
                <p className="text-[1.8rem] text-zinc-500">
                  Was{' '}
                  <span className="line-through">
                    {formatCurrency(ogPrice)}
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        {!isVehicleCategory(product.category) && (
          <>
            {displayedShipping ? (
              <>
                {isFreeShipping && !product.is_digital ? (
                  <div className="relative mt-2 flex items-center gap-x-2">
                    <div className="flex items-center gap-2">
                      <div className="w-fit rounded-2xl bg-black px-4 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[1.4rem] font-semibold uppercase text-white">
                            Free Shipping
                          </span>
                        </div>
                      </div>

                      <span className="text-[1.4rem] text-black">to</span>

                      <span className="text-[1.4rem] font-medium text-black">
                        {getCountryLabelFromCode(displayedShipping.code)}
                      </span>
                    </div>

                    {!!product.shipping_costs?.length && (
                      <>
                        {!displayedShipping.estimated ? (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Show shipping options"
                            onClick={() =>
                              setShowShippingOptions(!showShippingOptions)
                            }
                          >
                            <ChevronDownIcon
                              className={cn(
                                'h-8 w-8 text-zinc-700',
                                showShippingOptions ? 'rotate-180' : ''
                              )}
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Redo estimate"
                            onClick={() => {
                              if (isMobile()) {
                                setShowMobileEstimateShipping(true);
                              } else {
                                setShowDesktopEstimateShipping(true);
                              }
                            }}
                          >
                            <RefreshCwIcon className="h-8 w-8 text-zinc-700" />
                          </button>
                        )}
                      </>
                    )}

                    {showShippingOptions && (
                      <div className="absolute right-0 top-full z-10 w-full cursor-default rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-6 py-2 shadow-md">
                        {product.shipping_costs?.map(({ code, cost }) => (
                          <div
                            className="flex items-center gap-x-2 py-2 text-[1.6rem]"
                            key={code}
                          >
                            + {formatCurrency(cost)} Shipping ({code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : !product.is_digital ? (
                  <div className="relative mt-2 flex items-center gap-x-2">
                    <div className="flex items-center gap-4 font-medium">
                      <span className="text-[1.4rem] text-zinc-500">
                        + {formatCurrency(displayedShipping.cost)} Shipping to{' '}
                        {getCountryLabelFromCode(displayedShipping.code)}
                      </span>
                    </div>

                    {!!product.shipping_costs?.length && (
                      <>
                        {!displayedShipping.estimated ? (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Show shipping options"
                            onClick={() =>
                              setShowShippingOptions(!showShippingOptions)
                            }
                          >
                            <ChevronDownIcon
                              className={cn(
                                'h-8 w-8 text-zinc-700',
                                showShippingOptions ? 'rotate-180' : ''
                              )}
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="border-none bg-inherit"
                            aria-label="Redo estimate"
                            onClick={() => {
                              if (isMobile()) {
                                setShowMobileEstimateShipping(true);
                              } else {
                                setShowDesktopEstimateShipping(true);
                              }
                            }}
                          >
                            <RefreshCwIcon className="h-8 w-8 text-zinc-700" />
                          </button>
                        )}
                      </>
                    )}

                    {showShippingOptions && (
                      <div className="absolute right-0 top-full z-10 w-full cursor-default rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-6 py-2 shadow-md">
                        {product.shipping_costs?.map(({ code, cost }) => (
                          <div
                            className="flex items-center gap-x-2 py-2 text-[1.6rem]"
                            key={code}
                          >
                            + {formatCurrency(cost)} Shipping ({code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </>
            ) : displayedShipping === null ? (
              <>
                <button
                  type="button"
                  className="mt-2 text-start text-[1.4rem] text-zinc-500 underline hover:text-zinc-700"
                  onClick={() => {
                    if (isMobile()) {
                      setShowMobileEstimateShipping(true);
                    } else {
                      setShowDesktopEstimateShipping(true);
                    }
                  }}
                >
                  + est. shipping
                </button>
              </>
            ) : (
              <div className="h-6 w-48 animate-pulse rounded-xl bg-brand-lightest-gray" />
            )}
          </>
        )}
      </div>

      {!isVehicleCategory(product.category) && (
        <ProductCouponDisplay product={product} />
      )}

      <DesktopEstimateShipping
        product={product}
        isOpen={showDesktopEstimateShipping}
        dismiss={(shippingCost) => {
          setEstimatedShipping(shippingCost);
          setShowDesktopEstimateShipping(false);
        }}
      />

      <MobileEstimateShipping
        product={product}
        show={showMobileEstimateShipping}
        dismiss={(shippingCost) => {
          setEstimatedShipping(shippingCost);
          setShowMobileEstimateShipping(false);
        }}
      />
    </div>
  );
};

export default ProductPriceAndShipping;
